import { handleMultiProductImages } from '@/utils';
import classNames from 'classnames';
import EfImg from 'elem/ui/ef-img';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';
import './index.scss';

export interface EfSkuItemProps {
    /**
     * productCatagoryId
     */
    productCatagoryId: string;
    /**
     * spuId
     */
    spuId: string;
    /**
     * skuId
     */
    skuId: string;
    /**
     * sn
     */
    sn: string;
    /**
     * buyLink
     */
    buyLink?: string;
    /**
     * enable
     */
    enable?: boolean;
    /**
     * generalKey
     */
    generalKey: string;
    /**
     * genericSpec
     */
    genericSpec?: string;
    /**
     * images
     */
    images?: string;
    /**
     * model
     */
    model?: string;
    /**
     * name
     */
    name: string;
    /**
     * ownSpec
     */
    ownSpec?: string;
    /**
     * price
     */
    price: number;
    /**
     * productType
     */
    productType?: string;
    /**
     * unit
     */
    unit?: string;
    /**
     * warrantyEnable
     */
    warrantyEnable?: boolean;
    /**
     * [key: string]: any;  其他可能存在的任意字段
     */
    [key: string]: any;
}

export interface EfProductDetailItemProps {
    /**
     * spuId: SPU级别产品id
     */
    spuId: string;
    /**
     * name: 产品名称
     */
    name: string;
    /**
     * name: 产品图
     */
    images?: string | any;
    /**
     * skuList: 产品sku列表(长度3)
     */
    skuList: [EfSkuItemProps, EfSkuItemProps, EfSkuItemProps];
    /**
     * [key: string]: any;  其他可能存在的任意字段
     */
    [key: string]: any;
}

export interface EfStyleJsonProps {
    /**
     * firstIcon
     */
    firstIcon?: string;
    /**
     * firstType
     */
    firstType?: string;
    /**
     * secondIcon
     */
    secondIcon?: string;
    /**
     * secondType
     */
    secondType: string;
    /**
     * [key: string]: any;  其他可能存在的任意字段
     */
    [key: string]: any;
}

export interface EfSpecParamItemPros {
    /**
     * fieldType
     */
    fieldType: string;
    /**
     * generalKey
     */
    generalKey: string;
    /**
     * sort
     */
    sort?: number;
    /**
     * name
     */
    name: string;
    /**
     * styleJson
     */
    styleJson?: EfStyleJsonProps;
    /**
     * unit
     */
    unit?: string;
    /**
     * [key: string]: any;  其他可能存在的任意字段
     */
    [key: string]: any;
}

export interface EfProductCatagoryItemProps {
    /**
     * generalKey
     */
    generalKey: string;
    /**
     * name
     */
    name: string;
    /**
     * newSpecparamList
     */
    newSpecparamList: EfSpecParamItemPros[];
    /**
     * productCatagoryId
     */
    productCatagoryId: string;
    /**
     * specGroupId
     */
    specGroupId: string;
    /**
     * name
     */
    specParamList: EfSpecParamItemPros[];
    /**
     * [key: string]: any;  其他可能存在的任意字段
     */
    [key: string]: any;
}

export interface EfProductMultiSpecProps {
    /**
     * 主题色theme
     */
    theme?: 'black' | 'white';
    /**
     * 样式名
     */
    className?: string;
    /**
     * 产品详情列表（纵向)
     */
    items: EfProductDetailItemProps[];
    /**
     * 参数品类列表（横向)
     */
    catagories: EfProductCatagoryItemProps[];
}

/**
 * 维护: kalkin
 *
 * RIVER 2系列 / DELTA 3犀利 那种多产品SPEC对比, 支持任意个产品
 */
const EfProductMultiSpec: React.FC<EfProductMultiSpecProps> = ({ items, catagories, className, theme = 'white' }) => {
    const prefixCls = `ef-product-multi-spec`;
    const prefix = `${prefixCls}-`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-products-${items.length}`]: items.length,
    });
    const { locale = 'us' } = useRouter();
    const [top, setTop] = useState(0);

    const formatImg = (productDetail: EfProductDetailItemProps) => {
        const imgObj = JSON.parse(typeof productDetail?.images === 'string' ? productDetail?.images : JSON.stringify(productDetail?.images) || '{}');
        let skuListData: any;
        if (productDetail?.skuList?.length > 0) {
            skuListData = productDetail.skuList;
        } else {
            skuListData = [
                {
                    images: handleMultiProductImages(productDetail?.images, 'specs'),
                    name: productDetail?.name,
                },
            ];
        }
        // 多列产品的特殊情况： 优先使用transparentImg
        console.log(imgObj);
        return items.length > 1
            ? imgObj?.transparentImages || imgObj?.spaceImages || imgObj?.images || skuListData[0]?.images
            : imgObj?.spaceImages || imgObj?.images || imgObj?.transparentImages || skuListData[0]?.images;
    };

    const generateSpecValue = (product: any, group: any, param: any) => {
        const ownSpec = product && product.skuList && product.skuList?.length && product?.skuList[0]?.ownSpec;
        const ownSpecObj = JSON.parse(typeof ownSpec === 'string' ? ownSpec : JSON.stringify(ownSpec) || '{}');
        const ownSpecGroup = ownSpecObj[group?.specGroupId];
        const ownSpecGroupObj = JSON.parse(typeof ownSpecGroup === 'string' ? ownSpecGroup : JSON.stringify(ownSpecGroup) || '{}');
        let specV = ownSpecGroupObj[param?.specParamId];
        if (param?.fieldType === 'boolean') {
            const v = !specV ? '-' : ['false'].includes(specV) ? '-' : '✓';
            return v;
        }
        if (!Number.isNaN(Number(specV))) {
            specV = specV + (param?.unit || '');
        }
        return specV || '-';
    };

    const renderFrostNav = () => {
        let initScrollTop = 0;
        const regionPopupEl = document.querySelector('.regionPopup') as HTMLElement;
        if (regionPopupEl) {
            const styles = window.getComputedStyle(regionPopupEl);
            const position = styles.position;
            initScrollTop += Number(position === 'sticky' ? regionPopupEl?.offsetHeight : 0) || 0;
        }
        const headerEl = document.querySelector('.ecoflow-frame-header') as HTMLElement;
        if (headerEl) {
            const styles = window.getComputedStyle(headerEl);
            const position = styles.position;
            initScrollTop += Number(position === 'sticky' ? headerEl?.offsetHeight : 0) || 0;
        }
        const naver1El = document.querySelector('.ef-product-naver') as HTMLElement;
        if (naver1El) {
            const styles = window.getComputedStyle(naver1El);
            const position = styles.position;
            initScrollTop += Number(position === 'sticky' ? naver1El?.offsetHeight : 0) || 0;
        }
        const naver2El = document.querySelector('.ef-nav-product') as HTMLElement;
        if (naver2El) {
            const styles = window.getComputedStyle(naver2El);
            const position = styles.position;
            initScrollTop += Number(position === 'sticky' ? naver2El?.offsetHeight : 0) || 0;
        }
        setTop(initScrollTop);
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const productSpecNames = document.querySelector('.ef-product-multi-spec .ef-product-multi-spec-names-box-sticky') as HTMLElement;
        productSpecNames?.classList.toggle('frost', scrollTop > initScrollTop);
    };

    const scrollA = (e: any) => {
        const { scrollLeft } = e.target;
        const scrollElementB = document.querySelector('.ef-product-multi-spec .ef-product-multi-spec-inner .ef-product-multi-spec-inner-box') as HTMLDivElement;
        if (scrollElementB) {
            scrollElementB.scrollLeft = scrollLeft;
        }
    };

    const scrollB = (e: any) => {
        const { scrollLeft } = e.target;
        const scrollElementA = document.querySelector(
            '.ef-product-multi-spec .ef-product-multi-spec-names-box-sticky .ef-product-multi-spec-names-box',
        ) as HTMLDivElement;
        if (scrollElementA) {
            scrollElementA.scrollLeft = scrollLeft;
        }
    };

    useEffect(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (scrollTop > 0) {
            document.querySelector('.ef-product-multi-spec .ef-product-multi-spec-names-box-sticky')?.classList.add('frost');
        }
        document.addEventListener('scroll', renderFrostNav);

        const scrlllElementA = document.querySelector(
            '.ef-product-multi-spec .ef-product-multi-spec-names-box-sticky .ef-product-multi-spec-names-box',
        ) as HTMLDivElement;
        scrlllElementA.addEventListener('scroll', scrollA);

        const scrlllElementB = document.querySelector('.ef-product-multi-spec .ef-product-multi-spec-inner .ef-product-multi-spec-inner-box') as HTMLDivElement;
        scrlllElementB.addEventListener('scroll', scrollB);
        return () => {
            document.removeEventListener('scroll', renderFrostNav);
            scrlllElementA.removeEventListener('scroll', scrollA);
            scrlllElementB.removeEventListener('scroll', scrollB);
        };
    }, []);

    return (
        <div className={classes}>
            <div className={`${prefix}names-box-sticky`} style={{ top: top + 'px' }}>
                <div className={`${prefix}names-box`}>
                    <ul className={`${prefix}names`}>
                        {items?.map((item: EfProductDetailItemProps, k: number) => (
                            <li className={`${prefix}name`} key={k}>
                                {item?.name}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className={`${prefix}inner`}>
                <div className={`${prefix}inner-box`}>
                    <ul className={`${prefix}imgs`}>
                        {items?.map((item: EfProductDetailItemProps, k: number) => (
                            <li className={`${prefix}img`} key={k}>
                                <EfImg pc={formatImg(item)} />
                            </li>
                        ))}
                    </ul>
                    <ul className={`${prefix}groups`}>
                        {catagories?.map(
                            (group: EfProductCatagoryItemProps, k: number) =>
                                group?.specParamList?.length > 0 && (
                                    <li className={`${prefix}group`} key={group?.specGroupId + '_' + k}>
                                        <p className={`${prefix}group-name`}>{group?.name}</p>
                                        <ul className={`${prefix}specs`}>
                                            {group?.specParamList?.map((param: EfSpecParamItemPros, m: number) => (
                                                <li className={`${prefix}spec`} key={group?.specGroupId + '_' + k + '_' + param?.specParamId + '_' + m}>
                                                    <ul className={`${prefix}spec-products`}>
                                                        {items?.map((item: EfProductDetailItemProps, n: number) => {
                                                            const specV = generateSpecValue(item, group, param);
                                                            // flag: me-ar 全局direction: rlt了，但是spec中并不是所有的需要revert
                                                            const flag = locale === 'sa' && String(specV).endsWith(' [* *]');
                                                            return (
                                                                <li
                                                                    className={`${prefix}spec-product`}
                                                                    key={
                                                                        group?.specGroupId +
                                                                        '_' +
                                                                        k +
                                                                        '_' +
                                                                        param?.specParamId +
                                                                        '_' +
                                                                        m +
                                                                        '_' +
                                                                        item?.spuId +
                                                                        '_' +
                                                                        n
                                                                    }
                                                                >
                                                                    <p className={`${prefix}spec-name`}>{param?.name}</p>
                                                                    <p className={`${prefix}spec-value${flag ? ' ltr' : ''}`}>
                                                                        {flag ? String(specV).split(' [* *]') : specV}
                                                                    </p>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ),
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default EfProductMultiSpec;
